import {useMemo, useState, useEffect} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Row, Col, Table, Button } from 'react-bootstrap';
import GrantEntitlementModal from '../modals/GrantEntitlementModal';
import API from '../helpers/api';
import moment from 'moment';
import ChangeEmailAddressModal from '../modals/ChangeEmailAddressModal';
import ChangePasswordModal from '../modals/ChangePasswordModal';


export default function CustomerDetail() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const api = useMemo(() => new API({dispatch}), [dispatch]);
    const [customer, setCustomer] = useState(null);
    const [entitlements, setEntitlements] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [supportLogs, setSupportLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showGrantEntitlementModal, setShowGrantEntitlementModal] = useState(false);
    const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

    const fetchCustomerDetail = async () => {
        if (!id) return;
        try {
            const response = await api.getAccountInformationById(id);
            console.log("Customer Detail Response: ", response);
            setCustomer(response.accountData || null);
            setEntitlements(response.entitlements || []);
            setSubscriptions(response.subscriptions || []);
            //Sort support logs by createdDate descending
            response.supportLogs && response.supportLogs.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
            setSupportLogs(response.supportLogs || []);
            setIsLoading(false);
        } catch (error) {
            alert("Error fetching customer detail. Please try again.");
            console.error("Error fetching customer detail:", error);
        }
    }
    useEffect(() => {
        fetchCustomerDetail();
    }, [id]);

    const verifyAccount = async () => {
        if (!customer || !customer.accountReference) return;
        const confirmed = window.confirm(`Are you sure you want to verify the account for ${customer.email}?`);
        if (!confirmed) return;
        try {
            const success = await api.verifyAccount(customer.accountReference);
            if (success){
                alert("Account verified successfully.");
                fetchCustomerDetail();
            } else {
                alert("Error verifying account. Please try again.");
            }
        } catch(error){
            alert("Error verifying account. Please try again.");
            console.error("Error verifying account:", error);
        }
    }

    const triggerForgotPassword = async () => {
        if (!customer || !customer.email) return;
        const confirmed = window.confirm(`Are you sure you want to send a forgot password email to ${customer.email}?`);
        if (!confirmed) return;
        try {
            const success = await api.triggerForgotPassword(customer.email);
            if (success){
                alert("Forgot password email sent successfully.");
            } else {
                alert("Error sending forgot password email. Please try again.");
            }
        } catch(error){
            alert("Error sending forgot password email. Please try again.");
            console.error("Error sending forgot password email:", error);
        }
    }

    const resendVerificationEmail = async () => {
        if (!customer || !customer.accountReference) return;
        const confirmed = window.confirm(`Are you sure you want to resend the verification email to ${customer.email}?`);
        if (!confirmed) return;
        try {
            const success = await api.resendVerificationEmail(customer.accountReference);
            if (success){
                alert("Verification email resent successfully.");
            } else {
                alert("Error resending verification email. Please try again.");
            }
        } catch(error){
            alert("Error resending verification email. Please try again.");
            console.error("Error resending verification email:", error);
        }
    }

    const buildCustomerDetailSection = () => {
        if (!customer) return null;
        return (
            <div className="mb-3">
                <Row>
                    <Col md={4} className="mb-3">
                        <strong>CUSTOMER ID:</strong><br/>
                        {customer.clientUserId || 'N/A'}
                    </Col>

                    <Col md={4} className="mb-3">
                        <strong>CUSTOMER NAME:</strong><br/>
                        {customer.firstName && customer.lastName ? `${customer.firstName} ${customer.lastName}` : 'N/A'}
                    </Col>

                    <Col md={4} className="mb-3">
                        <strong>EMAIL ADDRESS:</strong><br/>
                        {customer.email || 'N/A'} <br/>
                        <Button variant="link" onClick={() => setShowChangeEmailModal(true)}>Change Email</Button>
                        { customer.email ? (<>
                            <br/>
                            <Button variant="link" onClick={triggerForgotPassword}>Send Forgot Password Email</Button>
                        </>) : null}
                        <br/>
                        <Button variant="link" onClick={() => setShowChangePasswordModal(true)}>Change Password</Button>
                    </Col>
                </Row>
                <Row className="mt-3">
                    <Col md={4} className="mb-3">
                        <strong>VERIFIED:</strong><br/>
                        {customer.verified ? 'Yes' : 'No'}
                        {!customer.verified && customer.email ? (<>
                            <br/>
                            <Button variant="link" onClick={resendVerificationEmail}>Resend Verification Email</Button>
                        </>) : null}
                        {!customer.verified && customer.accountReference ? (<>
                            <br/>
                            <Button variant="link" onClick={verifyAccount}>Verify Account</Button>
                        </>) : null}
                    </Col>
                    <Col md={4} className="mb-3">
                        <strong>CREATED ON:</strong><br/>
                        {moment(customer.createdDate).format('MMMM Do YYYY - hh:mm A') || 'N/A'}
                    </Col>

                    <Col md={4} className="mb-3">
                        <strong>LAST UPDATED:</strong><br/>
                        {moment(customer.lastUpdated).format('MMMM Do YYYY - hh:mm A') || 'N/A'}
                    </Col>
                </Row>
            </div>
        )
    }


    const buildSubscriptionsSection = () => {
        let section = null;
        if (!subscriptions || subscriptions.length === 0) {
            section = (
                <p>No subscriptions found.</p>
            );
        } else {
            section = (
                <Table striped bordered hover variant="dark" responsive>
                    <thead>
                        <tr>
                            <th>Group</th>
                            <th>Name</th>
                            <th>Cost</th>
                            <th>Payment Method</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Auto Renew</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {subscriptions.map((sub, index) => (
                            <tr key={index}>
                                <td>{sub.contractInfo.group || 'N/A'}</td>
                                <td>{sub.contractInfo.title || 'N/A'}</td>
                                <td>${sub.recurringPaymentInfo.subscribedPrice}</td>
                                <td>{sub.paymentMethod}</td>
                                <td>{moment(sub.startDate).format('MM/DD/YYYY') || 'N/A'}</td>
                                <td>{moment(sub.expiryDate).format('MM/DD/YYYY') || 'N/A'}</td>
                                <td>{sub.recurringPaymentInfo.autoRenewStatus == "Active" ? 'Yes' : 'No'}</td>
                                <td>{sub.contractInfo.status || 'N/A'}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )
        }


        return (
            <div className="mt-5">
                <h5 className="text-center">Subscriptions</h5>
                {section}
            </div>
        )

    }

    const buildSupportLogsSection = () => {
        let section = null;
        if (!supportLogs || supportLogs.length === 0) {
            section = (
                <p>No support logs found.</p>
            );
        } else {
            section = (
                <Table striped bordered hover variant="dark" responsive>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th>Title</th>
                            <th>Details</th>
                            <th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {supportLogs.map((log, index) => (
                            <tr key={index}>
                                <td>{moment(log.createdDate).format('MM/DD/YYYY hh:mm A') || 'N/A'}</td>
                                <td>{log.logTitle || 'N/A'}</td>
                                <td>{log.logDetails || 'N/A'}</td>
                                <td>{log.logStatus || 'N/A'}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            );
        }

        return (
            <div className="mt-5">
                <h5 className="text-center">Support Logs</h5>
                {section}
            </div>
        );
    }

    const buildEntitlementsSection = () => {
        let section = null;
        if (!entitlements || entitlements.length === 0) {
            section = (
                <p>No entitlements found.</p>
            );
        } else {
            section = (
                <Table striped bordered hover variant="dark" responsive>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Identifier</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                        </tr>
                    </thead>

                    <tbody>
                        {entitlements.map((ent, index) => (
                            <tr key={index}>
                                <td>{ent.name || 'N/A'}</td>
                                <td>{ent.identifier || 'N/A'}</td>
                                <td>{moment(ent.startDate).format('MM/DD/YYYY hh:mm A') || 'N/A'}</td>
                                <td>{moment(ent.endDate).format('MM/DD/YYYY hh:mm A') || 'N/A'}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )
        }

        return (
            <div className="mt-5">
                <h5 className="text-center">Entitlements</h5>
                <div className="text-center mb-3">
                    <Button variant="primary" onClick={() => setShowGrantEntitlementModal(true)}>Grant Entitlement</Button>
                </div>
                {section}
            </div>
        )
    }
    if (isLoading){
        return (
            <Container className="mt-5">
                <Card bg="dark" text="white">
                <Card.Header><h5>Loading Customer Detail...</h5></Card.Header>
                <Card.Body>
                    Please Wait...
                </Card.Body>
                </Card>
            </Container>
        )
    } else if (!customer){
        return (
            <Container className="mt-5">
                <Card bg="dark" text="white">
                <Card.Header><h5>Customer Not Found</h5></Card.Header>
                <Card.Body>
                    No customer found with the provided ID.
                </Card.Body>
                </Card>
            </Container>
        )
    }

    let modal = null;
    if (showGrantEntitlementModal){
        modal = <GrantEntitlementModal 
            accountId={customer.accountReference}
            accountInfo={customer}
            close={() => {
                setShowGrantEntitlementModal(false);
                fetchCustomerDetail();}}
        />
    } else if (showChangeEmailModal){
        modal = <ChangeEmailAddressModal 
            accountId={customer.accountReference}
            close={() => {
                setShowChangeEmailModal(false);
                fetchCustomerDetail();
            }}
        />
    } else if (showChangePasswordModal) {
        modal = <ChangePasswordModal
            accountId={customer.accountReference}
            close={() => {
                setShowChangePasswordModal(false);
                fetchCustomerDetail();
            }}
        />
    }

    return (
        <Container className="mt-5">
            {modal}
            <Card bg="dark" text="white">
            <Card.Header><h5>Customer Detail</h5></Card.Header>
            <Card.Body>
                {buildCustomerDetailSection()}
                <hr/>
                {buildSubscriptionsSection()}
                <hr/>
                {buildEntitlementsSection()}
                <hr/>
                {buildSupportLogsSection()}
            </Card.Body>
            </Card>
        </Container>
    )
}